function _async_generator(gen) {
    var front, back;
    function send(key, arg) {
        return new Promise(function(resolve, reject) {
            var request = {
                key: key,
                arg: arg,
                resolve: resolve,
                reject: reject,
                next: null
            };
            if (back) {
                back = back.next = request;
            } else {
                front = back = request;
                resume(key, arg);
            }
        });
    }
    function resume(key, arg) {
        try {
            var result = gen[key](arg);
            var value = result.value;
            var wrappedAwait = value instanceof _await_value;
            Promise.resolve(wrappedAwait ? value.wrapped : value).then(function(arg) {
                if (wrappedAwait) {
                    resume("next", arg);
                    return;
                }
                settle(result.done ? "return" : "normal", arg);
            }, function(err) {
                resume("throw", err);
            });
        } catch (err) {
            settle("throw", err);
        }
    }
    function settle(type, value) {
        switch(type){
            case "return":
                front.resolve({
                    value: value,
                    done: true
                });
                break;
            case "throw":
                front.reject(value);
                break;
            default:
                front.resolve({
                    value: value,
                    done: false
                });
                break;
        }
        front = front.next;
        if (front) {
            resume(front.key, front.arg);
        } else {
            back = null;
        }
    }
    this._invoke = send;
    if (typeof gen.return !== "function") {
        this.return = undefined;
    }
}
if (typeof Symbol === "function" && Symbol.asyncIterator) {
    _async_generator.prototype[Symbol.asyncIterator] = function() {
        return this;
    };
}
_async_generator.prototype.next = function(arg) {
    return this._invoke("next", arg);
};
_async_generator.prototype.throw = function(arg) {
    return this._invoke("throw", arg);
};
_async_generator.prototype.return = function(arg) {
    return this._invoke("return", arg);
};
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _await_async_generator(value) {
    return new _await_value(value);
}
function _await_value(value) {
    this.wrapped = value;
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _wrap_async_generator(fn) {
    return function() {
        return new _async_generator(fn.apply(this, arguments));
    };
}
import * as React from "react";
import { Effect, pipe, Fiber, FiberId, Stream } from "effect";
import { v4 as uuidv4 } from "uuid";
import { isReactContext } from "../utils/react";
//* It is important to note that the runtime of the host component should always be provided to the dependency array, if runtimes higher up the tree are accessed using useRuntimeFn, useRuntime or useRuntimeSync. This is automatically done by WithRuntime its attachTo utility function and might be something to recommend, because this creates a good overview of how the component interacts with external runtimes.
/*
This hook returns a function that can be called to trigger an effect.
It returns a promise that resolves to the value of the effect.
*/ export function useRuntimeFn(context, fn, deps = []) {
    const runtime = React.useContext(context);
    const finalDeps = [
        runtime,
        ...deps
    ];
    const emitter = React.useMemo(()=>new EventEmitter(), finalDeps);
    // const queue = React.useMemo(() => Queue.unbounded<{ data: T }>(), finalDeps);
    // const effect0 = React.useMemo(
    //   () =>
    //     Effect.gen(function* () {
    //       const q = yield* queue;
    //       return Stream.fromQueue(q).pipe(
    //         Stream.tap(Console.log),
    //         Stream.runDrain
    //       );
    //     }),
    //   finalDeps
    // );
    //   queue.pipe(
    //     Effect.andThen(Stream.fromQueue),
    //     Effect.andThen(
    //       Stream.mapEffect(({ data }) => (Effect.isEffect(fn) ? fn : fn(data)))
    //     ),
    //     Effect.fork,
    //     Effect.andThen(Stream.tap(Console.log)),
    //     Stream.runDrain
    //   ),
    //   finalDeps
    // );
    const effect = React.useMemo(()=>pipe(Stream.fromAsyncIterable(createAsyncIterator(emitter), ()=>{}), Stream.mapEffect(({ data, eventId })=>{
            return pipe(Effect.sync(()=>Effect.isEffect(fn) ? fn : fn(data)), Effect.andThen(Effect.tap(emitter.resolve(eventId))));
        }), Stream.runDrain), finalDeps);
    // useRuntime(
    //   context,
    //   Effect.gen(function* () {
    //     const q = yield* Queue.take(yield* queue);
    //     console.log(q);
    //   }).pipe(Effect.forever),
    // queue.pipe(
    //   Effect.andThen(Queue.take),
    //   Effect.tap(() => Console.log('take')),
    //   Effect.forever
    // ),
    //   finalDeps
    // );
    useRuntime(context, effect, deps);
    // const emitFn = React.useCallback(
    //   (data: T) =>
    //     runtime!.runPromise(queue.pipe(Effect.andThen(Queue.offer({ data })))),
    //   [runtime]
    // );
    // return emitFn as IsUnknown<T> extends true
    //   ? () => Promise<A>
    //   : (value: T) => Promise<A>;
    return emitter.emit;
}
/*
This hook is used to run an effect in a runtime.
It takes a context and an effect and runs the effect in the runtime provided by the context. It is used by useRuntimeFn. Assumes createRuntimeContext is used to create the context, because it expects a Layer when withRuntime is missing.
*/ const noRuntimeMessage = `No runtime available. 
  Did you forget to wrap your component using WithRuntime?
  `;
export const useRuntime = (context, effect, deps = [])=>{
    let runtime = context;
    if (isReactContext(context)) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        runtime = React.useContext(context);
        if (runtime === undefined) throw new Error(noRuntimeMessage);
    }
    React.useEffect(()=>{
        const F = runtime.runFork(effect);
        return ()=>Effect.runSync(F.pipe(Fiber.interruptAsFork(FiberId.none)));
    }, [
        runtime,
        ...deps
    ]);
};
/*
This hook is used to run an effect synchronously in a runtime and return the value of the effect.
It takes a context and an effect and runs the effect in the runtime provided by the context.
*/ export const useRuntimeSync = (context, effect, deps = [])=>{
    const runtime = React.useContext(context);
    if (runtime === undefined) throw new Error(noRuntimeMessage);
    return React.useMemo(()=>runtime.runSync(effect), [
        ...deps,
        runtime
    ]);
};
/* 
This is converting push based events to a pull based stream, 
where the consumer has control through the provided effect.
*/ // TODO: consider if we can use Stream.buffer instead of EventEmitter.
class EventEmitter {
    subscribe(listener) {
        this.listeners.push(listener);
        this.notifyListeners();
    }
    notifyListeners() {
        while(this.eventQueue.length > 0 && this.listeners.length > 0){
            const event = this.eventQueue.shift();
            this.listeners.forEach((listener)=>listener(event.data, event.eventId));
        }
    }
    resolve(eventId) {
        return (result)=>{
            const resolver = this.resolvers.get(eventId);
            if (resolver) {
                resolver(result);
                this.resolvers.delete(eventId);
            }
        };
    }
    waitForEvent() {
        var _this = this;
        return _async_to_generator(function*() {
            if (_this.eventQueue.length > 0) {
                return Promise.resolve(_this.eventQueue.shift());
            }
            return new Promise((resolve)=>{
                const oneTimeListener = (data, eventId)=>{
                    resolve({
                        data,
                        eventId
                    });
                    _this.listeners = _this.listeners.filter((l)=>l !== oneTimeListener);
                };
                _this.subscribe(oneTimeListener);
            });
        })();
    }
    constructor(){
        _define_property(this, "listeners", []);
        _define_property(this, "resolvers", new Map());
        _define_property(this, "eventQueue", []);
        _define_property(this, "emit", (data)=>{
            const eventId = uuidv4();
            let resolver;
            const promise = new Promise((resolve)=>{
                resolver = resolve;
            });
            this.eventQueue.push({
                data,
                eventId
            });
            this.notifyListeners();
            this.resolvers.set(eventId, resolver);
            return promise;
        });
    }
}
function createAsyncIterator(emitter) {
    return _createAsyncIterator.apply(this, arguments);
}
function _createAsyncIterator() {
    _createAsyncIterator = _wrap_async_generator(function*(emitter) {
        while(true){
            yield yield _await_async_generator(emitter.waitForEvent());
        }
    });
    return _createAsyncIterator.apply(this, arguments);
}
